import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Users, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_META = {
  info: { label: 'Info', badge: 'bg-slate-100 text-slate-700' },
  warning: { label: 'Warning', badge: 'bg-amber-100 text-amber-800' },
  urgent: { label: 'Urgent', badge: 'bg-red-100 text-red-700' },
};

const AnnouncementDetailModal = ({ id, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/announcements/${id}`, { withCredentials: true });
        setData(r.data);
      } catch { toast.error('Could not load'); }
      finally { setLoading(false); }
    })();
  }, [id]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="announcement-detail-modal">
      <div className="bg-white rounded-md shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Delivery details</h3>
          <button onClick={onClose} data-testid="announcement-detail-close" className="p-1 rounded hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : !data ? (
          <div className="p-10 text-center text-slate-500">Not available.</div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${PRIORITY_META[data.priority]?.badge}`}>
                  {PRIORITY_META[data.priority]?.label}
                </span>
                <span className="text-xs text-slate-500">{new Date(data.created_at).toLocaleString()}</span>
              </div>
              <h4 className="text-xl font-bold text-slate-900">{data.title}</h4>
              <p className="text-sm text-slate-700 whitespace-pre-wrap mt-2">{data.body}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-slate-500" />
                <span className="font-semibold text-slate-700">Read {data.stats?.read || 0} of {data.stats?.total || 0}</span>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                {(data.recipients || []).map((r) => (
                  <div key={r.user_id} className="flex items-center justify-between py-2" data-testid={`recipient-row-${r.user_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{r.name || r.email}</p>
                      <p className="text-xs text-slate-500">{r.role} · {r.email}</p>
                    </div>
                    {r.acknowledged ? (
                      <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {new Date(r.read_at).toLocaleString()}</span>
                    ) : (
                      <span className="text-xs text-slate-400">Not yet read</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementDetailModal;
